import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import {
  getDefaultPrivacyCategories,
  isGlobalPrivacyControlEnabled,
  loadPrivacyPreferences,
  PRIVACY_PREFERENCES_KEY,
  savePrivacyPreferences,
  syncRuntimePrivacyPreferences
} from '@/privacy/privacyPreferences'
import { useOverlayCoordinator } from '@contexts/OverlayCoordinatorContext'
import { OVERLAY_IDS } from '@utils/overlayIds'

const PrivacyPreferencesContext = createContext(null)

export function PrivacyPreferencesProvider({ children }) {
  const [record, setRecord] = useState(loadPrivacyPreferences)
  const { activeOverlayId, closeOverlay, openOverlay } = useOverlayCoordinator()
  const globalPrivacyControl = isGlobalPrivacyControlEnabled()
  const categories = record?.categories || getDefaultPrivacyCategories()
  const isSettingsOpen = activeOverlayId === OVERLAY_IDS.PRIVACY_SETTINGS

  useEffect(() => {
    if (!globalPrivacyControl || !record?.categories.analytics) return

    const updatedRecord = savePrivacyPreferences(
      { ...record.categories, analytics: false },
      'global-privacy-control'
    )
    setRecord(updatedRecord)
  }, [globalPrivacyControl, record])

  useEffect(() => {
    const handleStorage = event => {
      if (event.key !== PRIVACY_PREFERENCES_KEY && event.key !== null) return

      const updatedRecord = loadPrivacyPreferences()
      syncRuntimePrivacyPreferences(updatedRecord)
      setRecord(updatedRecord)
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  useEffect(
    () => () => closeOverlay(OVERLAY_IDS.PRIVACY_SETTINGS),
    [closeOverlay]
  )

  const persist = useCallback(
    (nextCategories, source) => {
      const updatedRecord = savePrivacyPreferences(nextCategories, source)
      setRecord(updatedRecord)
      closeOverlay(OVERLAY_IDS.PRIVACY_SETTINGS)
      return updatedRecord
    },
    [closeOverlay]
  )

  const acceptAll = useCallback(
    () => persist({ experience: true, analytics: true }, 'accept-all'),
    [persist]
  )

  const rejectOptional = useCallback(
    () => persist(getDefaultPrivacyCategories(), 'reject-optional'),
    [persist]
  )

  const saveCategories = useCallback(
    nextCategories => persist(nextCategories, 'preferences'),
    [persist]
  )

  const openSettings = useCallback(
    () => openOverlay(OVERLAY_IDS.PRIVACY_SETTINGS),
    [openOverlay]
  )
  const closeSettings = useCallback(
    () => closeOverlay(OVERLAY_IDS.PRIVACY_SETTINGS),
    [closeOverlay]
  )

  const value = useMemo(
    () => ({
      acceptAll,
      activeOverlayId,
      categories,
      closeSettings,
      globalPrivacyControl,
      hasDecision: Boolean(record),
      isSettingsOpen,
      openSettings,
      record,
      rejectOptional,
      saveCategories
    }),
    [
      acceptAll,
      activeOverlayId,
      categories,
      closeSettings,
      globalPrivacyControl,
      isSettingsOpen,
      openSettings,
      record,
      rejectOptional,
      saveCategories
    ]
  )

  return (
    <PrivacyPreferencesContext.Provider value={value}>
      {children}
    </PrivacyPreferencesContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePrivacyPreferences() {
  const context = useContext(PrivacyPreferencesContext)

  if (!context) {
    throw new Error(
      'usePrivacyPreferences must be used within a PrivacyPreferencesProvider'
    )
  }

  return context
}
